/**
 * Bundle 57.1 hotfix - shared error-message extractor.
 *
 * Root cause of the "[object Object]" alert on the Zelle payment
 * step: FastAPI validation errors return `{"detail": [{loc, msg,
 * type}, ...]}` (an array of objects). Blindly stringifying that
 * with `alert(err.response.data.detail)` renders `[object Object]`.
 *
 * This helper accepts any thrown value - axios error, plain Error,
 * a string, undefined - and returns a human-readable message. Use
 * it at every user-facing catch site.
 */
export function extractErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
  if (err == null) return fallback;
  if (typeof err === 'string') return err;

  // Axios shape: err.response.data.{detail|message|error}
  const data = err?.response?.data;
  if (data) {
    // FastAPI structured validation: detail is an array of
    // {loc, msg, type}. Join the msg fields.
    if (Array.isArray(data.detail)) {
      const parts = data.detail
        .map((d) => (typeof d === 'string' ? d : d?.msg || d?.message || ''))
        .filter(Boolean);
      if (parts.length) return parts.join(', ');
    }
    // FastAPI single-string detail is the common path.
    if (typeof data.detail === 'string') return data.detail;
    // Nested error object (e.g. Bundle 51 profile-gate: `{detail: {message, current, required}}`).
    if (data.detail && typeof data.detail === 'object') {
      if (typeof data.detail.message === 'string') return data.detail.message;
    }
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string') return data.error;
  }

  // Plain Error or thrown object with a `message`.
  if (typeof err?.message === 'string' && err.message) return err.message;

  return fallback;
}

export default extractErrorMessage;
